import React from 'react';

// Client-side column sorting for the tables that hold all their rows in memory —
// the same contract as usePagination: feed it the fully-filtered row array, get a
// sorted array back, then paginate that. Sort keys are either an object field
// name or an array index (for screens whose rows are arrays). A third click on a
// column clears the sort (back to the natural order).
//
//   const { sorted, sortKey, sortDir, requestSort } = useSort(filteredRows);
//   ... usePagination(sorted) ...
//   <SortableTh label="CVE" sortKey="cve" sortKey.../>
//
// Values are compared numerically when both look numeric, as Dates when both
// parse as dates, else case-insensitively as strings. null/undefined sort last.
const _num = (v) => {
	if (v == null || v === '') return null;
	const n = typeof v === 'number' ? v : Number(String(v).replace(/[, ]/g, ''));
	return Number.isFinite(n) ? n : null;
};
const _date = (v) => {
	if (v == null || v === '') return null;
	const t = Date.parse(v);
	return Number.isNaN(t) ? null : t;
};

// Severity sorts by rank, not alphabetically (so CRITICAL doesn't land before
// HIGH). Recognised automatically: a value that is one of these words is ranked;
// anything else falls through to the generic comparison, so non-severity columns
// are untouched. Ascending = least→most severe; click again for most-severe-first.
const _SEV_RANK = { critical: 5, high: 4, medium: 3, moderate: 3, warning: 3,
                    low: 2, info: 1, informational: 1, none: 0, unknown: 0 };
const _sev = (v) => {
	if (v == null) return null;
	const key = String(v).trim().toLowerCase();
	return key in _SEV_RANK ? _SEV_RANK[key] : null;
};

// Connection status sorts by rank, not alphabetically, so "Online" leads
// "Offline" (which it would not alphabetically: 'f' < 'n'). Ascending = Online
// first — the useful default for a fleet page. Only these two exact words are
// ranked; every other value falls through to the generic comparison, so columns
// that merely contain other words are untouched.
const _STATUS_RANK = { online: 0, offline: 1 };
const _status = (v) => {
	if (v == null) return null;
	const key = String(v).trim().toLowerCase();
	return key in _STATUS_RANK ? _STATUS_RANK[key] : null;
};

const compare = (a, b) => {
	// null/undefined always last, regardless of direction.
	const aNull = a == null || a === '';
	const bNull = b == null || b === '';
	if (aNull && bNull) return 0;
	if (aNull) return 1;
	if (bNull) return -1;

	const asv = _sev(a), bsv = _sev(b);
	if (asv != null && bsv != null) return asv - bsv;

	const ast = _status(a), bst = _status(b);
	if (ast != null && bst != null) return ast - bst;

	const an = _num(a), bn = _num(b);
	if (an != null && bn != null) return an - bn;

	const ad = _date(a), bd = _date(b);
	if (ad != null && bd != null) return ad - bd;

	return String(a).toLowerCase().localeCompare(String(b).toLowerCase(), undefined, { numeric: true });
};

export default function useSort(rows, initial = null) {
	const [sortKey, setSortKey] = React.useState(initial ? initial.key : null);
	const [sortDir, setSortDir] = React.useState(initial ? (initial.dir || 'asc') : null);

	const requestSort = React.useCallback((key) => {
		setSortKey((prevKey) => {
			if (prevKey !== key) { setSortDir('asc'); return key; }
			// same column: asc -> desc -> off
			setSortDir((prevDir) => (prevDir === 'asc' ? 'desc' : null));
			return key;
		});
	}, []);

	// Set a specific sort directly (e.g. a page applying its default once its
	// columns load). Unlike requestSort it does not toggle, so callers stay in
	// control of the direction.
	const setSort = React.useCallback((key, dir = 'asc') => {
		setSortKey(key);
		setSortDir(dir);
	}, []);

	const sorted = React.useMemo(() => {
		if (!sortKey || !sortDir || !Array.isArray(rows)) return rows;
		const dir = sortDir === 'desc' ? -1 : 1;
		// Stable sort: decorate with the original index so equal rows keep order.
		return rows
			.map((r, i) => [r, i])
			.sort((x, y) => {
				const c = compare(x[0] ? x[0][sortKey] : undefined, y[0] ? y[0][sortKey] : undefined);
				return c !== 0 ? c * dir : x[1] - y[1];
			})
			.map((pair) => pair[0]);
	}, [rows, sortKey, sortDir]);

	return { sorted, sortKey, sortDir, requestSort, setSort };
}
